import NewReviewForm from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/NewReviewForm";
import styled, { useTheme } from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import GroupsListMessage from "features/newMarketplace/NewLook/GroupsListMessage.tsx";
import { useState } from "react";
import Empty from "common/Empty/Empty";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import ReviewsList from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/ReviewsList";
import CurrentUserReview from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/CurrentUserReview";
import React from "react";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface ReviewsTabContentProps {
  subgroupId: number;
}

const ReviewsTabContent = ({ subgroupId }: ReviewsTabContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();
  const { data, isFetching, isSuccess, isError, refetch } =
    sharedLib.sharedApi.marketplace.useGetGroupReviewsQuery(
      {
        group_id: subgroupId,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const [isEdit, setIsEdit] = useState(false);
  const [reviewInput, setReviewInput] = useState("");
  const [reviewStars, setReviewStars] = useState(0);
  const firstReview = data?.reviews?.[0];
  const isUserLeftReview = firstReview?.current_user_comment;

  const showNewReviewForm =
    ((data?.allow_review || data?.disallowed_review_reason) &&
      !isUserLeftReview &&
      data?.days_until_review <= 0) ||
    isEdit;

  const onEditReview = (
    rejectedReviewInput: string,
    rejectedReviewStars: number
  ) => {
    setIsEdit(true);
    setReviewInput(rejectedReviewInput);
    setReviewStars(rejectedReviewStars);
  };

  const getCurrentUserReview = () => {
    if (isUserLeftReview) {
      return (
        <CurrentUserReview
          currentUserReview={firstReview}
          isEdit={isEdit}
          onEdit={() => onEditReview(firstReview.message, firstReview.stars)}
        />
      );
    }
  };

  if (((isSuccess && !data) || isError) && isNewLook) {
    return (
      <Panel flush={true}>
        <GroupsListMessage
          title={t("errors.group_title")}
          description={t("errors.description")}
          actionLabel={t("errors.retry")}
          onAction={refetch}
        />
      </Panel>
    );
  }

  if ((isSuccess && !data) || isError) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  if (isNewLook && isFetching && !data) {
    return (
      <NewLookSkeleton aria-busy={true}>
        <Skeleton width="100%" height={180} radius={12} />
        <Skeleton width="100%" height={240} radius={12} />
      </NewLookSkeleton>
    );
  }

  const getReviewWarning = () => {
    const daysUntilReview = data?.days_until_review;
    let message;
    if (daysUntilReview && daysUntilReview > 1 && daysUntilReview < 8)
      message = t("reviews.review_warning_week");
    else if (daysUntilReview === 1)
      message = t("reviews.review_warning_tomorrow");

    if (message && isNewLook) return <NewLookMessage>{message}</NewLookMessage>;
    if (message)
      return (
        <Flex $justifyContent="center">
          <MessageTitle>{message}</MessageTitle>
        </Flex>
      );
  };

  return (
    <ReviewsTabWrapper>
      <LoadingSpinnerOverlay
        isLoading={!isNewLook && isFetching}
        spinnerSize="large"
        spinnerMarginTop={100}
      >
        {getReviewWarning()}
        {getCurrentUserReview()}
        {showNewReviewForm && (
          <NewReviewForm
            subgroupId={subgroupId}
            reviewIsEdit={isEdit}
            reviewInput={reviewInput}
            stars={reviewStars}
            onSubmit={() => setIsEdit(false)}
            disallowedReviewReason={data?.disallowed_review_reason}
          />
        )}
        {data && (
          <ReviewsList
            subGroupReviews={data.reviews}
            reviewsLoaded={isSuccess}
          />
        )}
      </LoadingSpinnerOverlay>
    </ReviewsTabWrapper>
  );
};

export default ReviewsTabContent;

const ReviewsTabWrapper = styled.div`
  position: relative;
`;
const NewLookSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const NewLookMessage = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: left;
`;
const MessageTitle = styled(Title).attrs({ level: 4, type: "secondary" })`
  margin-bottom: 16px;
`;
